import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Link,
    Search,
    Copy,
    BarChart3,
    ExternalLink
} from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import { createShortUrl, getUrlClicks, getLongUrl, ApiError } from '@/services/urlShortenerApi';

const REDIRECT_BASE_URL = 'https://app.thenewscc.com.br/redirect?url=';

interface UrlHistory {
    shortId: string;
    longUrl: string;
    clicks: number;
    createdAt: Date;
}

const URLShortener: React.FC = () => {
    const [longUrl, setLongUrl] = useState('');
    const [shortIdToSearch, setShortIdToSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [urlHistory, setUrlHistory] = useState<UrlHistory[]>([]);
    const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
   // const updateIntervalRef = useRef<NodeJS.Timeout>();
    const CACHE_DURATION = 300000; // Aumentado para 5 minutos
    const [_isLoading, setIsLoading] = useState(false);
    const loadingRef = useRef(false);

    const isApiError = (response: any): response is ApiError => {
        return 'error' in response;
    };

    const loadUrlClicks = useCallback(async (force: boolean = false) => {
        const now = new Date();
        if (!force && lastUpdate && now.getTime() - lastUpdate.getTime() < CACHE_DURATION) {
            return;
        }

        // Evita múltiplas requisições simultâneas
        if (loadingRef.current) {
            return;
        }

        loadingRef.current = true;
        setIsLoading(true);

        try {
            const clicksData = await getUrlClicks();
            if (!isApiError(clicksData)) {
                const existingUrls = new Map(urlHistory.map(url => [url.shortId, url]));
                
                const updatedUrls = clicksData.urls.map(apiUrl => {
                    const existingUrl = existingUrls.get(apiUrl.shortId);
                    if (existingUrl) {
                        return { ...existingUrl, clicks: apiUrl.clicks };
                    } else {
                        return {
                            shortId: apiUrl.shortId,
                            longUrl: apiUrl.longUrl,
                            clicks: apiUrl.clicks,
                            createdAt: new Date(apiUrl.createdAt)
                        };
                    }
                });

                setUrlHistory(updatedUrls);
                setLastUpdate(now);
            } else {
                toast.error("Erro ao carregar dados", {
                    description: "Não foi possível atualizar as estatísticas de cliques"
                });
            }
        } catch (error) {
            console.error('Erro ao carregar clicks:', error);
            toast.error("Erro ao carregar dados", {
                description: "Tente novamente mais tarde"
            });
        } finally {
            loadingRef.current = false;
            setIsLoading(false);
        }
    }, [urlHistory, lastUpdate]);

    useEffect(() => {
        loadUrlClicks(true);
        
        const intervalId = setInterval(() => {
            loadUrlClicks();
        }, CACHE_DURATION);
        
        return () => {
            clearInterval(intervalId);
            loadingRef.current = false;
        };
    }, [loadUrlClicks]);

    const handleCreateShortUrl = async () => {
        if (!longUrl) {
            toast.error("URL Inválida", {
                description: "Por favor, insira uma URL válida"
            });
            return;
        }

        setLoading(true);
        try {
            const result = await createShortUrl(longUrl);
            if (!isApiError(result)) {
                setUrlHistory(prev => [{
                    shortId: result.shortId,
                    longUrl: result.longUrl,
                    clicks: 0,
                    createdAt: new Date()
                }, ...prev]);
                
                toast.success("URL Encurtada com Sucesso!", {
                    description: `ID: ${result.shortId}`
                });
                setLongUrl('');
            } else {
                toast.error("Erro ao Encurtar URL", {
                    description: result.error
                });
            }
        } catch (error) {
            toast.error("Erro ao Encurtar URL", {
                description: "Tente novamente mais tarde"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearchUrl = async () => {
        if (!shortIdToSearch) {
            toast.error("ID Inválido", {
                description: "Por favor, insira um ID válido"
            });
            return;
        }

        setLoading(true);
        try {
            const urlData = await getLongUrl(shortIdToSearch);
            
            if (!isApiError(urlData)) {
                toast.success("URL Encontrada", {
                    description: urlData.longUrl
                });

                if (!urlHistory.some(url => url.shortId === shortIdToSearch)) {
                    const clicksData = await getUrlClicks();
                    const clicks = !isApiError(clicksData) 
                        ? clicksData.urls.find(u => u.shortId === shortIdToSearch)?.clicks || 0
                        : 0;

                    setUrlHistory(prev => [{
                        shortId: urlData.shortId,
                        longUrl: urlData.longUrl,
                        clicks,
                        createdAt: new Date()
                    }, ...prev]);
                }
            } else {
                toast.error("URL não encontrada", {
                    description: urlData.error
                });
            }
        } catch (error) {
            toast.error("Erro ao buscar URL", {
                description: "Tente novamente mais tarde"
            });
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (shortId: string) => {
        const fullUrl = `${REDIRECT_BASE_URL}${shortId}`;
        navigator.clipboard.writeText(fullUrl);
        toast.success("Copiado!", {
            description: "URL copiada para a área de transferência"
        });
    };

    return (
        <div className="container max-w-6xl mx-auto space-y-6 p-4">
            <div className="flex flex-col items-center justify-center mb-8">
                <span className="pointer-events-none whitespace-pre-wrap bg-gradient-to-b from-[#FF3366] to-[#FF99CC] bg-clip-text text-center text-6xl font-semibold leading-none text-transparent dark:from-[#FF3366] dark:to-[#FF1493]">
                    Encurtador de URLs
                </span>
            </div>

            {/* Métricas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                            <Link className="h-10 w-10 text-pink-500" />
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">URLs Encurtadas</p>
                                <h3 className="text-2xl font-bold dark:text-white">
                                    {urlHistory.length}
                                </h3>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                            <BarChart3 className="h-10 w-10 text-pink-500" />
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Total de Cliques</p>
                                <h3 className="text-2xl font-bold dark:text-white">
                                    {urlHistory.reduce((acc, curr) => acc + curr.clicks, 0)}
                                </h3>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                            <ExternalLink className="h-10 w-10 text-pink-500" />
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Média de Cliques</p>
                                <h3 className="text-2xl font-bold dark:text-white">
                                    {urlHistory.length > 0
                                        ? (urlHistory.reduce((acc, curr) => acc + curr.clicks, 0) / urlHistory.length).toFixed(1)
                                        : '0'}
                                </h3>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Formulário de Criação */}
            <Card>
                <CardHeader>
                    <CardTitle className="dark:text-white">Criar URL Curta</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex gap-4">
                        <Input
                            placeholder="Cole sua URL longa aqui"
                            value={longUrl}
                            onChange={(e) => setLongUrl(e.target.value)}
                        />
                        <Button
                            onClick={handleCreateShortUrl}
                            disabled={loading}
                            className="bg-pink-500 hover:bg-pink-600"
                        >
                            Encurtar
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Busca de URLs */}
            <Card>
                <CardHeader>
                    <CardTitle className="dark:text-white">Buscar URL</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex gap-4">
                        <Input
                            placeholder="Digite o ID da URL curta"
                            value={shortIdToSearch}
                            onChange={(e) => setShortIdToSearch(e.target.value)}
                        />
                        <Button
                            onClick={handleSearchUrl}
                            disabled={loading}
                            variant="outline"
                        >
                            <Search className="h-4 w-4 mr-2" />
                            Buscar
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Histórico */}
            <Card>
                <CardHeader>
                    <CardTitle className="dark:text-white">Histórico de URLs</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID Curto</TableHead>
                                <TableHead>URL Original</TableHead>
                                <TableHead>Cliques</TableHead>
                                <TableHead>Data de Criação</TableHead>
                                <TableHead>Ações</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {urlHistory.map((url) => (
                                <TableRow key={url.shortId}>
                                    <TableCell className="font-medium">
                                        <span className="text-sm text-gray-500">{REDIRECT_BASE_URL}</span>
                                        {url.shortId}
                                    </TableCell>
                                    <TableCell className="max-w-xs truncate">{url.longUrl}</TableCell>
                                    <TableCell>{url.clicks}</TableCell>
                                    <TableCell>
                                        {url.createdAt.toLocaleDateString('pt-BR')}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => copyToClipboard(url.shortId)}
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default URLShortener;
